//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import $ from "jquery";

import {
  IFacebookPagesSearchResponse,
  IInstagramLocationPageSearchResponse,
  IPageByIdResponse,
} from '@apis/gateway-instagram/facebookGraphApiSearchApiModel';
import { IFacebookPage } from '@models/IFacebookPage';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function facebookGraphApiSearchFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v2/instagram/search${endpoint}`,
    init,
  );
}

/**
 * Gets location suggestions from the FB Pages Search API based on the given search term and limit
 *
 * /api/v2/instagram/search/pages/location
 *
 * @returns List of locations as a promise
 */
export async function getLocationsFromPagesSearch(
  searchTerm: string,
  limit?: number,
  abortSignal?: AbortSignal,
): Promise<IInstagramLocationPageSearchResponse> {
  const paramsObj = {
    searchTerm,
    limit,
  };

  const params = $.param(paramsObj);
  const res = await facebookGraphApiSearchFetch(
    `/pages/location?${params}`,
    isDefined(abortSignal) ? { signal: abortSignal } : undefined,
  );
  return res.json();
}

/**
 * Search for facebook pages by query
 *
 * /api/v2/instagram/search/pages
 *
 * @returns List of Facebook pages as a promise
 */
export async function getPagesSearch(
  searchTerm: string,
  limit?: number,
  init?: RequestInit,
): Promise<IFacebookPage[]> {
  const params = makeParams({
    searchTerm,
    limit,
  });

  const res = await facebookGraphApiSearchFetch(`/pages?${params}`, init);
  const pageSearchResponse = await res.json<IFacebookPagesSearchResponse>();

  return pageSearchResponse.data;
}

/**
 * Search for facebook page by id
 *
 * /api/v2/instagram/search/page/:pageId
 *
 * @returns A Facebook page as a promise
 */
export async function getPageById(
  pageId: string,
  init?: RequestInit,
): Promise<IFacebookPage> {
  const res = await facebookGraphApiSearchFetch(`/page/${pageId}`, init);
  const pageSearchResponse = await res.json<IPageByIdResponse>();

  return pageSearchResponse.data;
}
